<template>
  <div class="geo-map-panel">
    <!-- ══ PANEL HEAD ══ -->
    <div class="map-panel-head">
      <div class="map-head-left">
        <v-icon size="16" color="#455a64" class="mr-2">mdi-map-outline</v-icon>
        <span class="map-panel-title">แผนที่เชิงภูมิศาสตร์อาชีวศึกษา</span>
        <v-chip x-small outlined color="#455a64" class="ml-2">Dual-Layer</v-chip>
      </div>
      <div class="map-head-right">
        <!-- Mode Tabs -->
        <div class="map-mode-tabs">
          <span
            v-for="m in mapModes"
            :key="m.key"
            :class="['map-mode-tab', { active: mapMode === m.key }]"
            @click="mapMode = m.key; refreshMap()"
          >
            <v-icon size="12" class="mr-1">{{ m.icon }}</v-icon>{{ m.label }}
          </span>
        </div>
        <!-- Zoom controls -->
        <div class="zoom-btns">
          <button class="zoom-btn" @click="mapZoomIn" title="ขยาย"><v-icon size="14">mdi-plus</v-icon></button>
          <button class="zoom-btn" @click="mapZoomOut" title="ย่อ"><v-icon size="14">mdi-minus</v-icon></button>
          <button class="zoom-btn" @click="mapFit" title="รีเซ็ต"><v-icon size="14">mdi-home-outline</v-icon></button>
        </div>
      </div>
    </div>

    <!-- ══ MAP BODY ══ -->
    <div class="map-body">
      <!-- Leaflet Map -->
      <div class="map-wrap" ref="mapWrap">
        <div id="th-map-dual" ref="thMap" class="th-map"></div>

        <!-- Bubble SVG Overlay (for proportional circles) -->
        <svg class="bubble-overlay" ref="bubbleSvg">
          <g v-for="b in visibleBubbles" :key="b.pid">
            <circle
              :cx="b.x" :cy="b.y" :r="b.r"
              :fill="b.bubbleColor"
              stroke="white"
              :stroke-width="b.r > 10 ? 1.5 : 1"
              opacity="0.82"
              class="bubble-circle"
              @mouseenter="showBubbleTip($event, b)"
              @mouseleave="hideTip"
              @click="onProvinceClick(b)"
              style="cursor:pointer"
            />
            <text
              v-if="b.r > 14"
              :x="b.x" :y="b.y + 4"
              text-anchor="middle"
              class="bubble-label"
            >{{ b.shortLabel }}</text>
          </g>
        </svg>

        <!-- Tooltip -->
        <transition name="tip-fade">
          <div
            v-if="tooltip.show"
            class="map-tooltip"
            :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
          >
            <div class="tip-header">
              <span class="tip-province">{{ tooltip.province }}</span>
              <span class="tip-region">{{ tooltip.region }}</span>
            </div>
            <div class="tip-body">
              <div class="tip-row">
                <span>นักเรียนรวม</span>
                <strong>{{ formatNum(tooltip.students) }} คน</strong>
              </div>
              <div class="tip-row">
                <span>ปวช.</span>
                <strong class="tc-blue">{{ formatNum(tooltip.ch) }}</strong>
              </div>
              <div class="tip-row">
                <span>ปวส.</span>
                <strong class="tc-purple">{{ formatNum(tooltip.hs) }}</strong>
              </div>
              <div class="tip-row">
                <span>วิทยาลัย</span>
                <strong>{{ tooltip.colleges }} แห่ง</strong>
              </div>
              <div class="tip-divider"></div>
              <div class="tip-row">
                <span>อัตรากำลังรวม</span>
                <strong>{{ formatNum(tooltip.manpower) }}</strong>
              </div>
              <div class="tip-row">
                <span>เกณฑ์รวม</span>
                <strong>{{ formatNum(tooltip.criterion) }}</strong>
              </div>
              <div class="tip-row">
                <span>ขาด / เกิน</span>
                <strong :class="tooltip.incomplete < 0 ? 'tc-red' : 'tc-green'">
                  {{ tooltip.incomplete < 0 ? '▼' : '▲' }}{{ formatNum(Math.abs(tooltip.incomplete)) }}
                </strong>
              </div>
              <div class="tip-row" v-if="tooltip.ratio !== '-'">
                <span>ครู : นร.</span>
                <strong>1 : {{ tooltip.ratio }}</strong>
              </div>
              <!-- Mini progress bar: ขาด/เกิน -->
              <div class="tip-bar-wrap">
                <div
                  class="tip-bar-fill"
                  :style="`width:${tooltip.incompletePct}%; background:${tooltip.incomplete < 0 ? '#dc2626' : '#2e7d32'}`"
                ></div>
              </div>
            </div>
          </div>
        </transition>

        <!-- Selected Province Info -->
        <transition name="tip-fade">
          <div v-if="selectedProv" class="selected-info">
            <div class="sel-title">
              <v-icon size="14" color="#455a64" class="mr-1">mdi-map-marker</v-icon>
              <strong>{{ selectedProv.province }}</strong>
            </div>
            <div class="sel-stats">
              <div class="sel-stat"><span>นร.</span><strong>{{ formatNum(selectedProv.students) }}</strong></div>
              <div class="sel-stat sel-stat--incomplete" :class="selectedProv.incomplete < 0 ? 'sel-stat--red' : 'sel-stat--green'">
                <span>ขาด/เกิน</span>
                <strong>{{ selectedProv.incomplete < 0 ? '▼' : '▲' }}{{ formatNum(Math.abs(selectedProv.incomplete)) }}</strong>
              </div>
            </div>
            <div class="sel-actions">
              <v-btn x-small text color="primary" @click="$emit('filter-province', selectedProv)">กรองข้อมูล</v-btn>
              <v-btn icon x-small @click="selectedProv = null"><v-icon size="12">mdi-close</v-icon></v-btn>
            </div>
          </div>
        </transition>
      </div>

      <!-- ══ RIGHT SIDE PANEL ══ -->
      <div class="map-side">

        <!-- LEGEND -->
        <div class="legend-box">
          <div class="legend-title">
            <v-icon size="13" color="#374151" class="mr-1">mdi-information-outline</v-icon>
            คำอธิบายสัญลักษณ์
          </div>

          <!-- Fill color legend -->
          <div class="legend-section">
            <div class="legend-sub">{{ fillLegendTitle }}</div>
            <div class="legend-grad-wrap">
              <div class="legend-grad" :style="`background: ${gradientCss}`"></div>
              <div class="legend-grad-labels">
                <span>{{ minLabel }}</span>
                <span>สมดุล</span>
                <span>{{ maxLabel }}</span>
              </div>
            </div>
          </div>

          <!-- Bubble size legend (shown only in student/teacher modes) -->
          <div class="legend-section" v-if="mapMode !== 'colleges'">
            <div class="legend-sub">ขนาดวงกลม = {{ bubbleLegendLabel }}</div>
            <div class="bubble-size-leg">
              <div v-for="s in bubbleSizeLegend" :key="s.label" class="bsl-item">
                <svg :width="s.r*2+4" :height="s.r*2+4">
                  <circle :cx="s.r+2" :cy="s.r+2" :r="s.r" fill="#455a64" opacity="0.6" stroke="white" stroke-width="1"/>
                </svg>
                <span>{{ s.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- PROVINCE RANKING -->
        <div class="ranking-box">
          <div class="ranking-title">
            <v-icon size="13" color="#374151" class="mr-1">mdi-format-list-numbered</v-icon>
            {{ rankingTitle }}
          </div>
          <div class="ranking-list">
            <div
              v-for="(p, i) in topProvinces"
              :key="p.province"
              class="rank-row"
              :class="{ 'rank-row--active': selectedProv && selectedProv.province === p.province }"
              @click="onRankClick(p)"
            >
              <span class="rank-num" :class="i < 3 ? 'rank-num--top' : ''">{{ i + 1 }}</span>
              <span class="rank-name">{{ p.province }}</span>
              <div class="rank-bar-wrap">
                <div class="rank-bar-fill" :style="`width:${p.pct}%;background:${p.barColor}`"></div>
              </div>
              <span class="rank-val" :style="`color:${p.barColor}`">{{ p.displayVal }}</span>
            </div>
          </div>
        </div>

        <!-- STATS SUMMARY -->
        <div class="map-stats-grid">
          <div v-for="s in mapStats" :key="s.label" class="map-stat-card">
            <div class="map-stat-val" :style="`color:${s.color}`">{{ s.value }}</div>
            <div class="map-stat-label">{{ s.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * GeoMapDashboard.vue
 * Dual-Layer Bivariate Choropleth Map
 *
 * Props:
 *   studentData   : Array  — rows from rate_work_college_admin.php (action:list)
 *   manpowerData  : Array  — rows from rate_work_g.php
 *   filterYear    : String — current year filter
 *
 * Emits:
 *   filter-province({ province, region, ... }) — เมื่อผู้ใช้คลิก "กรองข้อมูล"
 *
 * Dependencies (ต้องโหลดใน index.html หรือ main.js):
 *   Leaflet CSS + JS  (window.L)
 *   GeoJSON Thailand  (https://raw.githubusercontent.com/apisit/thailand.json/master/thailand.json)
 */

const API_KEY = 'HRvec2021'

/* ── Province metadata: province_name → centroid [lat, lng] ── */
const PROVINCE_CENTROIDS = {
  'กรุงเทพมหานคร':[13.753,100.504],'สมุทรปราการ':[13.599,100.597],'นนทบุรี':[13.86,100.512],
  'ปทุมธานี':[14.018,100.52],'พระนครศรีอยุธยา':[14.35,100.585],'อ่างทอง':[14.59,100.455],
  'ลพบุรี':[14.799,100.653],'สิงห์บุรี':[14.89,100.397],'ชัยนาท':[15.186,100.129],
  'สระบุรี':[14.529,100.907],'ชลบุรี':[13.361,100.985],'ระยอง':[12.681,101.281],
  'จันทบุรี':[12.611,102.104],'ตราด':[12.243,102.518],'ฉะเชิงเทรา':[13.689,101.077],
  'ปราจีนบุรี':[14.051,101.37],'นครนายก':[14.204,101.213],'สระแก้ว':[13.824,102.065],
  'นครราชสีมา':[14.973,102.098],'บุรีรัมย์':[14.994,103.102],'สุรินทร์':[14.882,103.493],
  'ศรีสะเกษ':[15.117,104.322],'อุบลราชธานี':[15.245,104.847],'ยโสธร':[15.793,104.145],
  'ชัยภูมิ':[15.807,102.032],'อำนาจเจริญ':[15.865,104.627],'บึงกาฬ':[18.36,103.648],
  'หนองบัวลำภู':[17.204,102.441],'ขอนแก่น':[16.432,102.836],'อุดรธานี':[17.41,102.787],
  'เลย':[17.486,101.722],'หนองคาย':[17.877,102.742],'มหาสารคาม':[16.185,103.3],
  'ร้อยเอ็ด':[16.054,103.652],'กาฬสินธุ์':[16.431,103.506],'สกลนคร':[17.155,104.136],
  'นครพนม':[17.392,104.773],'มุกดาหาร':[16.543,104.723],'เชียงใหม่':[18.788,98.984],
  'ลำพูน':[18.575,99.009],'ลำปาง':[18.289,99.491],'อุตรดิตถ์':[17.627,100.099],
  'แพร่':[18.145,100.141],'น่าน':[18.784,100.779],'พะเยา':[19.156,99.893],
  'เชียงราย':[19.908,99.831],'แม่ฮ่องสอน':[19.302,97.964],'นครสวรรค์':[15.703,100.137],
  'อุทัยธานี':[15.38,100.024],'กำแพงเพชร':[16.482,99.524],'ตาก':[16.879,99.126],
  'สุโขทัย':[17.007,99.825],'พิษณุโลก':[16.822,100.265],'พิจิตร':[16.441,100.348],
  'เพชรบูรณ์':[16.42,101.159],'ราชบุรี':[13.537,99.814],'กาญจนบุรี':[14.005,99.548],
  'สุพรรณบุรี':[14.472,100.117],'นครปฐม':[13.82,100.044],'สมุทรสาคร':[13.548,100.274],
  'สมุทรสงคราม':[13.409,100.002],'เพชรบุรี':[12.965,99.647],'ประจวบคีรีขันธ์':[11.806,99.798],
  'นครศรีธรรมราช':[8.432,99.963],'กระบี่':[8.093,98.906],'พังงา':[8.451,98.526],
  'ภูเก็ต':[7.89,98.398],'สุราษฎร์ธานี':[9.14,99.331],'ระนอง':[9.965,98.638],
  'ชุมพร':[10.493,99.18],'สงขลา':[7.189,100.595],'สตูล':[6.614,100.067],
  'ตรัง':[7.557,99.611],'พัทลุง':[7.616,100.082],'ปัตตานี':[6.870,101.246],
  'ยะลา':[6.535,101.279],'นราธิวาส':[6.425,101.824],'บึงกาฬ_dup':[18.36,103.648],
}

/* ── lerp color ── */
function lerp(a, b, t) {
  const ah=parseInt(a.slice(1),16), bh=parseInt(b.slice(1),16)
  const [ar,ag,ab]=[(ah>>16)&255,(ah>>8)&255,ah&255]
  const [br,bg,bb]=[(bh>>16)&255,(bh>>8)&255,bh&255]
  return '#'+[Math.round(ar+(br-ar)*t),Math.round(ag+(bg-ag)*t),Math.round(ab+(bb-ab)*t)].map(v=>v.toString(16).padStart(2,'0')).join('')
}

function imbalanceColor(incomplete, maxAbs) {
  if (!maxAbs) return '#f5f5f5'
  const t = Math.min(Math.abs(incomplete) / maxAbs, 1)
  if (incomplete < 0) return lerp('#fecaca', '#dc2626', t)
  if (incomplete > 0) return lerp('#81c784', '#2e7d32', t)
  return '#f5f5f5'
}
function studentColor(students, maxStudents) {
  if (!maxStudents) return '#f5f5f5'
  const t = Math.min(students / maxStudents, 1)
  return lerp('#e4e4e7', '#455a64', t)
}
function ratioColor(ratio) {
  if (!ratio || ratio === '-') return '#f5f5f5'
  const r = +ratio
  if (r > 20) return lerp('#fecaca', '#dc2626', Math.min((r-20)/20, 1))
  return lerp('#81c784', '#2e7d32', Math.max(0, (20-r)/20))
}
function collegeColor(colleges, maxColleges) {
  if (!maxColleges) return '#f5f5f5'
  const t = Math.min(colleges / maxColleges, 1)
  return lerp('#fff4dc', '#a86400', t)
}

export default {
  name: 'GeoMapDashboard',
  props: {
    studentData: { type: Array, default: () => [] },
    manpowerData: { type: Array, default: () => [] },
    filterYear: { type: String, default: '' },
  },
  data() {
    return {
      leafletMap: null,
      leafletGeoLayer: null,
      geoData: null,
      mapMode: 'imbalance', // imbalance | students | ratio | colleges
      tooltip: { show: false, x: 0, y: 0, province: '', region: '', students: 0, ch: 0, hs: 0, colleges: 0, manpower: 0, criterion: 0, incomplete: 0, ratio: '-', incompletePct: 0 },
      selectedProv: null,
      bubbles: [], // { pid, x, y, r, bubbleColor, shortLabel, ... all tooltip data }
      mapModes: [
        { key: 'imbalance', label: 'ขาด/เกินเกณฑ์', icon: 'mdi-balance-scale' },
        { key: 'students',  label: 'นักเรียน+ครู',  icon: 'mdi-account-group' },
        { key: 'ratio',     label: 'ครู:นร.ratio',  icon: 'mdi-human-male-board' },
        { key: 'colleges',  label: 'สถานศึกษา',    icon: 'mdi-office-building' },
      ],
    }
  },
  computed: {
    provinceMap() {
      const map = {}
      this.studentData.forEach(r => {
        const name = r.province_name
        if (!name) return
        if (!map[name]) map[name] = { province: name, region: r.region_name || '', ch: 0, hs: 0, students: 0, colleges: new Set(), province_ID: r.province_ID }
        const v = (+r.rate_work_field_study_lavel_1||0)+(+r.rate_work_field_study_lavel_2||0)+(+r.rate_work_field_study_lavel_3||0)
        if (r.rate_work_college_id_class==20) map[name].ch += v
        else if (r.rate_work_college_id_class==30) map[name].hs += v
        map[name].students += v
        map[name].colleges.add(r.rate_work_college_code)
      })
      const mpMap = {}
      this.manpowerData.forEach(r => {
        const pname = r.province_name
        if (!pname) return
        if (!mpMap[pname]) mpMap[pname] = { manpower: 0, criterion: 0, incomplete: 0, teachOfficial: 0 }
        mpMap[pname].manpower += +r.rate_man_power_all||0
        mpMap[pname].criterion += +r.rate_criterion_all||0
        mpMap[pname].incomplete += +r.rate_criterion_incomplete_all||0
        mpMap[pname].teachOfficial += +r.rate_teach_official_all||0
      })
      return Object.fromEntries(
        Object.entries(map).map(([k, v]) => {
          const mp = mpMap[k] || { manpower:0, criterion:0, incomplete:0, teachOfficial:0 }
          return [k, {
            ...v,
            colleges: v.colleges.size,
            manpower: mp.manpower,
            criterion: mp.criterion,
            incomplete: mp.incomplete,
            teachOfficial: mp.teachOfficial,
            ratio: mp.teachOfficial > 0 ? (v.students / mp.teachOfficial).toFixed(1) : '-'
          }]
        })
      )
    },
    maxStudents() { return Math.max(...Object.values(this.provinceMap).map(p=>p.students), 1) },
    maxColleges() { return Math.max(...Object.values(this.provinceMap).map(p=>p.colleges), 1) },
    maxAbs() { return Math.max(...Object.values(this.provinceMap).map(p=>Math.abs(p.incomplete)), 1) },

    fillLegendTitle() {
      return { imbalance:'อัตรากำลัง — ขาด/เกินเกณฑ์', students:'ความหนาแน่นนักเรียน', ratio:'อัตราส่วนครู:นักเรียน', colleges:'จำนวนสถานศึกษา' }[this.mapMode]
    },
    minLabel() { return { imbalance:'ขาดมาก', students:'น้อย', ratio:'สมดุล (≤20)', colleges:'น้อย' }[this.mapMode] },
    maxLabel() { return { imbalance:'เกินมาก', students:'มาก', ratio:'ขาดมาก (>20)', colleges:'มาก' }[this.mapMode] },
    gradientCss() {
      return { imbalance:'linear-gradient(90deg,#dc2626,#fca5a5,#f5f5f5,#81c784,#2e7d32)', students:'linear-gradient(90deg,#e4e4e7,#455a64)', ratio:'linear-gradient(90deg,#81c784,#f5f5f5,#dc2626)', colleges:'linear-gradient(90deg,#fff4dc,#a86400)' }[this.mapMode]
    },
    bubbleLegendLabel() { return { imbalance:'จำนวนนักเรียน', students:'จำนวนนักเรียน', ratio:'จำนวนนักเรียน', colleges:'จำนวนสถานศึกษา' }[this.mapMode] },
    bubbleSizeLegend() { return [{r:6,label:'น้อย'},{r:12,label:'กลาง'},{r:20,label:'มาก'}] },

    topProvinces() {
      const entries = Object.values(this.provinceMap)
      const mode = this.mapMode
      let sorted
      if (mode === 'imbalance') {
        sorted = [...entries].sort((a,b)=>a.incomplete-b.incomplete).slice(0,10)
        const maxA = Math.max(...sorted.map(p=>Math.abs(p.incomplete)),1)
        return sorted.map(p=>({
          province: p.province,
          displayVal: (p.incomplete<0?'▼':'▲')+this.formatNum(Math.abs(p.incomplete)),
          pct: Math.round(Math.abs(p.incomplete)/maxA*100),
          barColor: p.incomplete < 0 ? '#dc2626' : '#2e7d32',
          ...p
        }))
      }
      if (mode === 'students') {
        sorted = [...entries].sort((a,b)=>b.students-a.students).slice(0,10)
        return sorted.map(p=>({...p, province:p.province, displayVal:this.formatNum(p.students), pct:Math.round(p.students/this.maxStudents*100), barColor:'#455a64'}))
      }
      if (mode === 'ratio') {
        sorted = [...entries].filter(p=>p.ratio!=='-').sort((a,b)=>+b.ratio-(+a.ratio)).slice(0,10)
        return sorted.map(p=>({...p, province:p.province, displayVal:`1:${p.ratio}`, pct:Math.min(Math.round(+p.ratio/30*100),100), barColor:+p.ratio>20?'#dc2626':'#2e7d32'}))
      }
      sorted = [...entries].sort((a,b)=>b.colleges-a.colleges).slice(0,10)
      return sorted.map(p=>({...p, province:p.province, displayVal:`${p.colleges} แห่ง`, pct:Math.round(p.colleges/this.maxColleges*100), barColor:'#a86400'}))
    },
    rankingTitle() {
      return { imbalance:'Top 10 ขาดอัตรากำลังมากที่สุด', students:'Top 10 นักเรียนมากที่สุด', ratio:'Top 10 อัตราส่วนครู:นร. สูงสุด', colleges:'Top 10 สถานศึกษามากที่สุด' }[this.mapMode]
    },

    mapStats() {
      const vals = Object.values(this.provinceMap)
      const shortfall = vals.filter(p=>p.incomplete<0).length
      const surplus = vals.filter(p=>p.incomplete>0).length
      const totalMissing = vals.reduce((s,p)=>s+(p.incomplete<0?Math.abs(p.incomplete):0),0)
      return [
        { label:'จังหวัดขาดอัตรากำลัง', value: shortfall, color:'#dc2626' },
        { label:'จังหวัดเกินอัตรากำลัง', value: surplus, color:'#2e7d32' },
        { label:'รวมขาดสุทธิ', value: this.formatNum(totalMissing), color:'#a86400' },
        { label:'จังหวัดทั้งหมด', value: vals.length, color:'#455a64' },
      ]
    },
    visibleBubbles() { return this.bubbles },
  },
  watch: {
    studentData() { this.buildBubbles(); this.refreshMap() },
    manpowerData() { this.buildBubbles(); this.refreshMap() },
  },
  mounted() {
    this.$nextTick(async () => {
      await this.initMap()
      this.buildBubbles()
    })
  },
  beforeDestroy() {
    if (this.leafletMap) { this.leafletMap.remove(); this.leafletMap = null }
  },
  methods: {
    formatNum(n) { if (n===null||n===undefined) return '-'; return (+n).toLocaleString('th-TH') },

    async initMap() {
      if (!window.L) { console.warn('Leaflet not loaded'); return }
      const el = this.$refs.thMap
      if (!el || this.leafletMap) return
      this.leafletMap = window.L.map(el, {
        zoomControl: false, attributionControl: false,
        scrollWheelZoom: true, minZoom: 5, maxZoom: 11
      })
      this.leafletMap.fitBounds([[5.5,97.3],[20.5,105.7]], { padding: [8,8] })
      this.leafletMap.on('moveend zoomend', () => this.buildBubbles())
      try {
        const res = await fetch('https://raw.githubusercontent.com/apisit/thailand.json/master/thailand.json')
        this.geoData = await res.json()
        this.renderGeoLayer()
      } catch(e) { console.error('GeoJSON load error:', e) }
    },

    getProvinceColor(provinceName) {
      const p = this.provinceMap[provinceName]
      if (!p) return '#e4e4e7'
      if (this.mapMode === 'imbalance') return imbalanceColor(p.incomplete, this.maxAbs)
      if (this.mapMode === 'students') return studentColor(p.students, this.maxStudents)
      if (this.mapMode === 'ratio') return ratioColor(p.ratio)
      return collegeColor(p.colleges, this.maxColleges)
    },

    renderGeoLayer() {
      if (!this.leafletMap || !this.geoData) return
      if (this.leafletGeoLayer) { this.leafletGeoLayer.remove(); this.leafletGeoLayer = null }
      this.leafletGeoLayer = window.L.geoJSON(this.geoData, {
        style: f => ({
          fillColor: this.getProvinceColor(this.getProvName(f)),
          fillOpacity: 0.75,
          color: '#ffffff',
          weight: 0.8,
          opacity: 1,
        }),
        onEachFeature: (f, layer) => {
          const name = this.getProvName(f)
          layer.on({
            mousemove: e => this.onLayerHover(e, name),
            mouseout: () => { this.tooltip.show = false; this.leafletGeoLayer && this.leafletGeoLayer.resetStyle(layer) },
            click: () => { const p = this.provinceMap[name]; if (p) this.selectedProv = { ...p } }
          })
        }
      }).addTo(this.leafletMap)
    },

    refreshMap() {
      if (!this.leafletGeoLayer) return
      this.leafletGeoLayer.eachLayer(layer => {
        if (layer.feature) {
          layer.setStyle({ fillColor: this.getProvinceColor(this.getProvName(layer.feature)), fillOpacity: 0.75, color: '#ffffff', weight: 0.8 })
        }
      })
      this.buildBubbles()
    },

    getProvName(feature) {
      const p = feature.properties
      return p.prov_nameTH || p.name_th || p.name || p.NAME_1 || ''
    },

    onLayerHover(e, name) {
      const p = this.provinceMap[name]
      if (!p) return
      const wrap = this.$refs.mapWrap
      const rect = wrap ? wrap.getBoundingClientRect() : { left:0, top:0 }
      const x = e.originalEvent.clientX - rect.left + 16
      const y = e.originalEvent.clientY - rect.top - 90
      const maxAbs = this.maxAbs || 1
      this.tooltip = {
        show: true, x, y,
        province: p.province || name,
        region: p.region,
        students: p.students, ch: p.ch, hs: p.hs, colleges: p.colleges,
        manpower: p.manpower, criterion: p.criterion,
        incomplete: p.incomplete, ratio: p.ratio,
        incompletePct: Math.min(Math.round(Math.abs(p.incomplete)/maxAbs*100), 100),
      }
    },

    buildBubbles() {
      if (!this.leafletMap) return
      const svgEl = this.$refs.bubbleSvg
      if (!svgEl) return
      const rect = svgEl.getBoundingClientRect()
      const W = rect.width || 400, H = rect.height || 400
      const maxStudents = this.maxStudents || 1
      const maxColleges = this.maxColleges || 1
      const bubbles = []
      Object.entries(this.provinceMap).forEach(([name, p]) => {
        const centroid = PROVINCE_CENTROIDS[name]
        if (!centroid) return
        try {
          const pt = this.leafletMap.latLngToContainerPoint([centroid[0], centroid[1]])
          const px = pt.x, py = pt.y
          if (px < -20 || px > W+20 || py < -20 || py > H+20) return
          const bubbleVal = this.mapMode === 'colleges' ? p.colleges / maxColleges : p.students / maxStudents
          const r = Math.max(4, Math.round(bubbleVal * 28))
          const bubbleColor = this.mapMode === 'ratio'
            ? (p.ratio !== '-' && +p.ratio > 20 ? '#dc262660' : '#16a34a60')
            : '#2563eb55'
          bubbles.push({
            pid: name, x: px, y: py, r,
            bubbleColor,
            shortLabel: name.length > 4 ? name.slice(0,4) : name,
            ...p
          })
        } catch(e) { /* skip */ }
      })
      this.bubbles = bubbles
    },

    showBubbleTip(e, b) {
      const wrap = this.$refs.mapWrap
      const rect = wrap ? wrap.getBoundingClientRect() : { left:0, top:0 }
      const maxAbs = this.maxAbs || 1
      this.tooltip = {
        show: true,
        x: e.clientX - rect.left + 16,
        y: e.clientY - rect.top - 90,
        province: b.province || b.pid,
        region: b.region,
        students: b.students, ch: b.ch, hs: b.hs, colleges: b.colleges,
        manpower: b.manpower, criterion: b.criterion,
        incomplete: b.incomplete, ratio: b.ratio,
        incompletePct: Math.min(Math.round(Math.abs(b.incomplete)/maxAbs*100), 100),
      }
    },
    hideTip() { this.tooltip.show = false },

    onProvinceClick(b) {
      this.selectedProv = { ...b }
    },
    onRankClick(p) {
      this.selectedProv = { ...p }
      const centroid = PROVINCE_CENTROIDS[p.province]
      if (centroid && this.leafletMap) {
        this.leafletMap.flyTo(centroid, 9, { duration: 0.8 })
      }
    },

    mapZoomIn() { this.leafletMap && this.leafletMap.zoomIn() },
    mapZoomOut() { this.leafletMap && this.leafletMap.zoomOut() },
    mapFit() { this.leafletMap && this.leafletMap.fitBounds([[5.5,97.3],[20.5,105.7]],{padding:[8,8]}) },
  }
}
</script>

<style scoped>
/* ══ PANEL ══ */
.geo-map-panel {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e4e4e7;
  overflow: hidden;
  font-family: 'Sarabun', sans-serif;
}

/* ══ HEAD ══ */
.map-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 1px solid #e4e4e7;
  flex-wrap: wrap;
  gap: 8px;
}
.map-head-left { display: flex; align-items: center; }
.map-panel-title { font-size: .82rem; font-weight: 700; color: #263238; }
.map-head-right { display: flex; align-items: center; gap: 8px; }

/* ══ MODE TABS ══ */
.map-mode-tabs { display: flex; gap: 2px; background: #f5f5f5; border-radius: 7px; padding: 2px; }
.map-mode-tab {
  display: flex; align-items: center;
  padding: 4px 10px;
  font-size: .68rem; font-weight: 600; font-family: 'Sarabun', sans-serif;
  color: #6b7280; cursor: pointer; border-radius: 5px; transition: all .12s;
  white-space: nowrap;
}
.map-mode-tab.active { background: #455a64; color: #fff; }
.map-mode-tab .v-icon { color: inherit !important; }

/* ══ ZOOM ══ */
.zoom-btns { display: flex; gap: 2px; }
.zoom-btn {
  width: 26px; height: 26px; border-radius: 6px;
  border: 1px solid #d1d5db; background: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background .1s;
}
.zoom-btn:hover { background: #f5f5f5; }
.zoom-btn .v-icon { color: #374151 !important; }

/* ══ BODY ══ */
.map-body { display: flex; min-height: 480px; }
.map-wrap { flex: 1; position: relative; min-height: 480px; }
.th-map { width: 100%; height: 100%; min-height: 480px; }

/* ══ BUBBLE OVERLAY ══ */
.bubble-overlay {
  position: absolute; top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
  z-index: 400;
}
.bubble-circle { pointer-events: all; transition: r .2s, opacity .2s; }
.bubble-circle:hover { opacity: 1 !important; }
.bubble-label {
  font-size: 9px; fill: white; font-weight: 700;
  pointer-events: none; font-family: 'Sarabun', sans-serif;
}

/* ══ TOOLTIP ══ */
.map-tooltip {
  position: absolute;
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,.12);
  min-width: 200px;
  z-index: 800;
  pointer-events: none;
  font-family: 'Sarabun', sans-serif;
}
.tip-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 12px 6px;
  border-bottom: 1px solid #f5f5f5;
}
.tip-province { font-size: .85rem; font-weight: 700; color: #263238; }
.tip-region { font-size: .65rem; color: #6b7280; }
.tip-body { padding: 6px 12px 10px; display: flex; flex-direction: column; gap: 4px; }
.tip-row { display: flex; justify-content: space-between; font-size: .75rem; color: #4b5563; }
.tip-row strong { font-weight: 700; color: #263238; }
.tip-divider { border-top: 1px solid #f5f5f5; margin: 3px 0; }
.tip-bar-wrap { height: 4px; background: #f5f5f5; border-radius: 999px; overflow: hidden; margin-top: 4px; }
.tip-bar-fill { height: 100%; border-radius: 999px; transition: width .3s; }
.tc-blue { color: #455a64 !important; }
.tc-purple { color: #b3151b !important; }
.tc-red { color: #dc2626 !important; }
.tc-green { color: #2e7d32 !important; }

.tip-fade-enter-active, .tip-fade-leave-active { transition: opacity .15s; }
.tip-fade-enter, .tip-fade-leave-to { opacity: 0; }

/* ══ SELECTED INFO ══ */
.selected-info {
  position: absolute;
  bottom: 12px; left: 12px;
  background: #fff;
  border: 1px solid #e4e4e7;
  border-left: 3px solid #455a64;
  border-radius: 8px;
  padding: 8px 12px;
  z-index: 700;
  font-family: 'Sarabun', sans-serif;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
}
.sel-title { display: flex; align-items: center; font-size: .78rem; color: #263238; margin-bottom: 5px; }
.sel-stats { display: flex; gap: 12px; margin-bottom: 5px; }
.sel-stat { font-size: .72rem; color: #6b7280; display: flex; flex-direction: column; }
.sel-stat strong { font-size: .82rem; color: #263238; }
.sel-stat--red strong { color: #dc2626; }
.sel-stat--green strong { color: #2e7d32; }
.sel-actions { display: flex; align-items: center; justify-content: space-between; }

/* ══ SIDE PANEL ══ */
.map-side {
  width: 240px;
  border-left: 1px solid #e4e4e7;
  display: flex; flex-direction: column;
  background: #fafafa;
  overflow-y: auto;
  flex-shrink: 0;
}

/* ══ LEGEND ══ */
.legend-box { padding: 12px 14px; border-bottom: 1px solid #e4e4e7; }
.legend-title { font-size: .72rem; font-weight: 700; color: #374151; margin-bottom: 8px; display: flex; align-items: center; }
.legend-sub { font-size: .67rem; color: #6b7280; margin-bottom: 4px; }
.legend-section { margin-bottom: 8px; }
.legend-grad-wrap { display: flex; flex-direction: column; gap: 2px; }
.legend-grad { height: 10px; border-radius: 5px; }
.legend-grad-labels { display: flex; justify-content: space-between; font-size: .62rem; color: #9ca3af; }
.bubble-size-leg { display: flex; gap: 12px; align-items: flex-end; margin-top: 4px; }
.bsl-item { display: flex; flex-direction: column; align-items: center; gap: 3px; font-size: .65rem; color: #6b7280; }

/* ══ RANKING ══ */
.ranking-box { padding: 10px 14px; border-bottom: 1px solid #e4e4e7; flex: 1; }
.ranking-title { font-size: .72rem; font-weight: 700; color: #374151; margin-bottom: 8px; display: flex; align-items: center; }
.ranking-list { display: flex; flex-direction: column; gap: 3px; }
.rank-row {
  display: flex; align-items: center; gap: 5px;
  padding: 4px 6px; border-radius: 5px; cursor: pointer;
  transition: background .1s;
}
.rank-row:hover { background: #f5f5f5; }
.rank-row--active { background: #f5f5f5; }
.rank-num { width: 16px; height: 16px; border-radius: 50%; background: #f5f5f5; color: #6b7280; font-size: .62rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.rank-num--top { background: #fff4dc; color: #a86400; }
.rank-name { font-size: .72rem; color: #263238; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rank-bar-wrap { width: 40px; height: 4px; background: #f5f5f5; border-radius: 999px; overflow: hidden; flex-shrink: 0; }
.rank-bar-fill { height: 100%; border-radius: 999px; transition: width .3s; }
.rank-val { font-size: .65rem; font-weight: 700; min-width: 32px; text-align: right; flex-shrink: 0; }

/* ══ MAP STATS ══ */
.map-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: 10px 14px; }
.map-stat-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 7px; padding: 7px 9px; text-align: center; }
.map-stat-val { font-size: 1.05rem; font-weight: 800; font-family: 'IBM Plex Mono', monospace; }
.map-stat-label { font-size: .6rem; color: #6b7280; margin-top: 1px; }

/* ══ RESPONSIVE ══ */
@media (max-width: 900px) {
  .map-body { flex-direction: column; }
  .map-side { width: 100%; border-left: none; border-top: 1px solid #e4e4e7; max-height: 300px; }
  .map-stats-grid { grid-template-columns: repeat(4, 1fr); }
  .map-mode-tabs { flex-wrap: wrap; }
}
</style>